// @ts-check
/* eslint-env browser */
/**
 * Shows files being uploaded along with their current status
 */

const UploadInfo = class extends HTMLElement {
  /** @type {HTMLFormElement | null | undefined} */
  form = null
  /** @type {HTMLInputElement | null | undefined} */
  fileInput = null
  /** @type {HTMLButtonElement | null | undefined} */
  submitButton = null
  /** @type {HTMLFieldSetElement | null | undefined} */
  documentTypeFieldSet = null
  /** @type {HTMLDivElement | null | undefined} */
  fileInputTab = null

  connectedCallback () {
    this.form = this.closest('form')
    this.fileInput = this.form?.querySelector('[name="file"]')
    this.submitButton = this.form?.querySelector('#upload-docs-button')
    this.documentTypeFieldSet = this.form?.querySelector('.doc-type-fieldset')
    this.fileInputTab = this.form?.querySelector('#upload-tabs')

    this.form?.addEventListener('submit', async (evt) => {
      const files = [...(this.fileInput?.files || [])]

      // Use server-side validation to show errors if no files or url is provided
      if (files.length === 0) {
        return
      }

      evt.preventDefault()
      if (this.documentTypeFieldSet) {
        this.documentTypeFieldSet.hidden = true
      }
      if (this.submitButton) {
        this.submitButton.hidden = true
      }
      if (this.fileInputTab) {
        this.fileInputTab.hidden = true
      }
      await this.#uploadFiles()
      if (this.submitButton) {
        this.submitButton.hidden = false
      }
      if (this.documentTypeFieldSet) {
        this.documentTypeFieldSet.hidden = false
      }
      if (this.fileInputTab) {
        this.fileInputTab.hidden = false
      }

      const backButton = this.querySelector('.upload-info__back-button')
      backButton?.removeAttribute('hidden')
      this.#scrollUploadHeadingIntoView()
    })
  }

  #render (files) {
    this.innerHTML = `
        <h2 id="upload-info-heading" class="govuk-heading-m govuk-!-margin-top-7" tabindex="-1">
          Uploading files
          <span class="govuk-visually-hidden"> - will redirect to your organisation's extractions when complete</span>
        </h2>
        <p class="govuk-visually-hidden upload-info__live-results-info" aria-live="polite"></p>
        <ul class="govuk-task-list">
          ${[...Array(files.length)].map(() => `
            <li class="govuk-task-list__item">
              <div class="govuk-task-list__name-and-hint"></div>
              <div class="govuk-error-message" aria-live="assertive"></div>
              <div class="govuk-task-list__status">
                <strong class="govuk-tag govuk-tag--grey">
                  Queued
                </strong>
              </div>
            </li>
          `).join('')}
        </ul>
        <a href="../" class="govuk-button govuk-button--secondary upload-info__back-button" hidden>Back to your organisation's extractions</a>
      `

    const fileNames = this.querySelectorAll('.govuk-task-list__name-and-hint')
    fileNames.forEach((fileName, index) => {
      fileName.textContent = files[index].name
    })
    const errorMessages = this.querySelectorAll('.govuk-error-message')
    errorMessages.forEach((errorMessage, index) => {
      errorMessage.id = `error-message-${index}`
    })

    requestAnimationFrame(() => {
      document.getElementById('upload-info-heading')?.focus()
    })
  }

  async #uploadFiles () {
    const files = [...this.fileInput?.files || []]

    this.#render(files)
    const fileStatuses = this.querySelectorAll('.govuk-tag')
    const errorMessages = this.querySelectorAll('.govuk-error-message')
    /** @type {HTMLInputElement | null | undefined} */
    const checkedDocType = this.form?.querySelector('input[name="type"]:checked')
    const docType = checkedDocType?.value || ''
    const liveResultsInfo = this.querySelector('.upload-info__live-results-info')

    let completedFiles = 0
    let errorFiles = 0

    for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
      const file = files[fileIndex]

      const currentFileStatus = fileStatuses[fileIndex]

      currentFileStatus.className = 'govuk-tag govuk-tag--blue'
      currentFileStatus.textContent = 'Uploading'

      const formData = new FormData()
      formData.append('type', docType)
      formData.append(this.fileInput?.name || 'file', file)

      try {
        const response = await fetch(this.form?.action || '', {
          method: 'POST',
          body: formData,
          headers: { 'X-Requested-With': 'fetch' }
        })

        if (response.ok) {
          currentFileStatus.className = 'govuk-tag govuk-tag--green'
          currentFileStatus.textContent = 'Complete'
          completedFiles++
        } else {
          currentFileStatus.className = 'govuk-tag govuk-tag--red'
          currentFileStatus.textContent = 'Error'
          errorFiles++
          try {
            const data = await response.json()
            errorMessages[fileIndex].textContent = data.errorMessages.map((error) => error.text).join(', ')
          } catch (error) {
            const redirectedToUnauthorised = response.redirected && response.url.endsWith('/unauthorised')
            if (response.status === 401 || redirectedToUnauthorised) {
              errorMessages[fileIndex].textContent = 'Your session may have expired. Please sign in again.'
            } else {
              errorMessages[fileIndex].textContent = 'There was a problem uploading your document. Please try again.'
            }
          }
        }
      } catch (error) {
        currentFileStatus.className = 'govuk-tag govuk-tag--red'
        currentFileStatus.textContent = 'Error'
        errorFiles++
        errorMessages[fileIndex].textContent = 'Network error, please try again'
      } finally {
        if (liveResultsInfo) {
          liveResultsInfo.textContent = errorFiles > 0 ? `${completedFiles} files completed, ${errorFiles} files with errors` : `${completedFiles} files completed`
        }
      }
    }

    if (completedFiles === files.length) {
      window.location.href = '/'
      return
    }

    // clear the file input and reset the table when a new file is selected
    if (this.fileInput) {
      this.fileInput.value = ''
      this.fileInput.dispatchEvent(new Event('change'))
      this.fileInput.addEventListener('change', (evt) => {
        const target = evt.target
        if (target instanceof HTMLInputElement && target.files && target.files.length > 0) {
          this.innerHTML = ''
        }
      }, { once: true })
    }
  }

  #scrollUploadHeadingIntoView () {
    requestAnimationFrame(() => {
      const heading = document.getElementById('upload-info-heading')
      if (!heading) return
      heading.scrollIntoView({ block: 'start' })
      heading.focus()
    })
  }
}

customElements.define('upload-info', UploadInfo)
